import { useState, useEffect } from "react";
import { useFetchUtteranceOrder, useUpdateUtteranceOrder } from "src/queries/hooks/admin";
import { SEVERITY_VARIANTS } from "@consts";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import useAppStore from "src/stores/appStore";
import { hasPermissionSelector } from "src/stores/selectors/appSelectors";

const useUtteranceOrder = () => {
  const { data: utterences = [] } = useFetchUtteranceOrder();
  const [sortedUtterences, setSortedUtterences] = useState([]);
  const [initialOrder, setInitialOrder] = useState([]);
  const { mutate: updateUtteranceOrder, isPending } = useUpdateUtteranceOrder();
  const [feedbackConfig, setFeedbackConfig] = useState({ show: false, message: "", severity: SEVERITY_VARIANTS.SUCCESS });
  const hasPermission = useAppStore(hasPermissionSelector);

  useEffect(() => {
    if (utterences.length) {
      const sorted = utterences.slice().sort((a, b) => a.orderNum - b.orderNum);
      setSortedUtterences(sorted);
      setInitialOrder(sorted.map(item => item.specKey));
    }
  }, [utterences]);

  const handleDragEnd = (event, arrayMove) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setSortedUtterences((prevSortedUtterences) => {
        const oldIndex = prevSortedUtterences.findIndex(item => item.specKey === active.id);
        const newIndex = prevSortedUtterences.findIndex(item => item.specKey === over.id);
        return arrayMove(prevSortedUtterences, oldIndex, newIndex);
      });
    }
  };

  const handleUpdateUtteranceOrder = () => {
    const orderPayload = sortedUtterences.map((item, idx) => ({
      ...item,
      orderNum: idx,
    }));

    updateUtteranceOrder(
      { order: orderPayload },
      {
        onSuccess: () => {
          setInitialOrder(sortedUtterences.map(item => item.specKey));
          setFeedbackConfig({
            show: true,
            message: "Order saved successfully",
            severity: SEVERITY_VARIANTS.SUCCESS
          });
        },
        onError: () => {
          setFeedbackConfig({
            show: true,
            message: "Failed to save order",
            severity: SEVERITY_VARIANTS.ERROR
          });
        }
      }
    );
  };

  const isOrderChanged = initialOrder.length > 0 && sortedUtterences.some((item, idx) => item.specKey !== initialOrder[idx]);

  const isSaveChangesDisabled = !isOrderChanged || isPending || !hasPermission(USER_PERMISSIONS.ADMIN_PANEL.ORDER_COLLECTIONS.WRITE);

  return {
    sortedUtterences,
    setSortedUtterences,
    initialOrder,
    handleDragEnd,
    handleUpdateUtteranceOrder,
    feedbackConfig,
    setFeedbackConfig,
    isPending,
    isSaveChangesDisabled
  };
};

export default useUtteranceOrder;
